import Grid from "@shared/components/ui-kit/layout/Grid/Grid.web";
import GridItem from "@shared/components/ui-kit/layout/GridItem/GridItem.web";
import {
  DEFAULT_MAX_TICKET_PURCHASE_LIMIT,
  DEFAULT_MIN_TICKET_PURCHASE_LIMIT,
} from "@shared/constants/common";
import useWindowSize from "@shared/hooks/useWindowSize";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Select from "@ui-kit/form/Select/Select.web";
import Icon from "@ui-kit/icons/Icon.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useMemo } from "react";
import { BestAvailableQuantityInputProps } from "./BestAvailableQuantityInput.props";

export default function BestAvailableQuantityInput({
  minimum = DEFAULT_MIN_TICKET_PURCHASE_LIMIT,
  maximum = DEFAULT_MAX_TICKET_PURCHASE_LIMIT,
  value,
  onChange,
  withDropdown = false,
  asButton = false,
  onOpenTypeQuantitySelect,
}: BestAvailableQuantityInputProps) {
  const { width } = useWindowSize();
  const isMobile = width && width < breakpoints.sm;
  const { t } = useTranslation("seating");
  const itemQuantityValues = useMemo(() => {
    return Array.from(
      { length: maximum - minimum + 1 },
      (_, i) => i + minimum
    ).map((quantity) => ({
      id: quantity,
      name: `${quantity} ${quantity > 1 ? t("tickets") : t("ticket")}`,
    }));
  }, [minimum, maximum]);

  if (asButton) {
    return (
      <Button
        data-testid="best-available-seating-drawer-select-seats"
        onClick={onOpenTypeQuantitySelect}
        // style as select dropdown to handle edge case
        // where ticket type is not selected first
        variant="unstyled"
        border="1px solid"
        borderColor="gray.400"
        borderRadius="sm"
        paddingX="md"
        textAlign="left"
        width="100%"
        fontWeight="md"
        fontSize="md"
      >
        <Flex align="center" width="100%" justify="space-between">
          <Text>
            {value > 0
              ? t("{{quantity}} tickets", { quantity: value })
              : t("Select options")}
          </Text>
          <Icon.ChevronUp />
        </Flex>
      </Button>
    );
  }

  return (withDropdown ||
    maximum - minimum >= DEFAULT_MAX_TICKET_PURCHASE_LIMIT) &&
    !isMobile ? (
    <Flex flex={1} gap="sm">
      <Select
        id="ticketCount"
        data-testid="ticket-count"
        items={itemQuantityValues}
        placeholder={t("Select quantity")}
        selectedItem={{ id: value, name: String(value) }}
        onSelectedItemChange={({ selectedItem }) => {
          if (selectedItem) {
            onChange?.(selectedItem.id);
          }
        }}
      />
    </Flex>
  ) : (
    <Grid gridTemplateColumns={{ base: "1fr", sm: "repeat(4, 1fr)" }} gap="sm">
      {itemQuantityValues.map((quantity) => (
        <GridItem key={quantity.id}>
          <Button.Ghost
            key={quantity.id}
            data-testid={`best-available-quantity-option-${quantity.id}`}
            onClick={() => onChange?.(quantity.id)}
            width="100%"
            textDecor="none"
            border={{ sm: "1px solid" }}
            borderColor={{ sm: "gray.400" }}
          >
            {quantity.name}
          </Button.Ghost>
        </GridItem>
      ))}
    </Grid>
  );
}
